// Shared magnitude-aware number parsing for Otto's numeric threshold filters.
//
// Otto captures numeric thresholds as human phrases ("more than 50k SF", "over 1m"). Two capabilities turn
// those into applied filters — the reusable table_view row-filter (`tableCatalog.parseWildcardValue`) and the
// dashboard wildcard apply (`dashboardFilters/applyMapping`) — and BOTH need "50k" to mean 50,000, not 50.
// The old per-capability extraction grabbed the leading digit run and silently dropped everything after it,
// so the unit ("sf") AND the magnitude ("k") were both discarded. Dropping the unit is right; dropping the
// magnitude turned "area more than 50k" into "area more than 50" (i.e. every building). This helper is the
// single place that understands magnitudes, so the two paths stay consistent.

// Compact suffixes (50k, 1.5m, $5MM) and their spelled-out forms (50 thousand, 1.5 million). Values in
// building data are counts, dollars, and square footage, so k/m/b/t cover every scale the dashboard shows.
const MAGNITUDES = [
    { tokens: ["k", "thousand", "thousands"], mult: 1e3 },
    { tokens: ["m", "mm", "mil", "million", "millions"], mult: 1e6 },
    { tokens: ["b", "bn", "billion", "billions"], mult: 1e9 },
    { tokens: ["t", "trillion", "trillions"], mult: 1e12 }
];
const MULT_BY_TOKEN = MAGNITUDES.reduce((acc, { tokens, mult }) => {
    tokens.forEach(t => (acc[t] = mult));
    return acc;
}, {});
// Longest-first alternation so "million" isn't shadowed by "mil"/"m" and the trailing \b anchors on the
// right token ("50 military" must NOT read as 50M — "m"/"mil" fail the boundary, so no magnitude applies).
const MAG_ALTERNATION = Object.keys(MULT_BY_TOKEN)
    .sort((a, b) => b.length - a.length)
    .join("|");

// First numeric token in a phrase, with optional thousands separators ("50,000"), a decimal ("1.5"), and a
// magnitude suffix/word attached to it ("50k", "1.5 million"). The magnitude must end on a word boundary, so
// unit noise after the number ("sf", "sq ft", "%") is left untouched. Only the FIRST number is read — a range
// ("between 10k and 20k") is expressed as a value_range upstream, not parsed here.
const NUMBER_RE = new RegExp(`(-?\\d[\\d,]*(?:\\.\\d+)?)\\s*(${MAG_ALTERNATION})?\\b`, "i");

/**
 * Expand a magnitude-bearing value to its full number.
 *   parseMagnitudeNumber("50k")            -> 50000
 *   parseMagnitudeNumber("more than 1.5m") -> 1500000   (operator words are ignored — only the number is read)
 *   parseMagnitudeNumber("1,000,000")      -> 1000000
 *   parseMagnitudeNumber(50000)            -> 50000      (already numeric — passed through)
 *   parseMagnitudeNumber("north wing")     -> null       (no number to extract)
 * Returns a Number, or null when the input carries no parseable number.
 */
export const parseMagnitudeNumber = input => {
    if (typeof input === "number") return Number.isFinite(input) ? input : null;
    const match = String(input == null ? "" : input)
        .toLowerCase()
        .match(NUMBER_RE);
    if (!match) return null;
    const digits = Number(match[1].replace(/,/g, ""));
    if (!Number.isFinite(digits)) return null;
    const mult = match[2] ? MULT_BY_TOKEN[match[2]] : 1;
    const value = digits * mult;
    // Integer results (the overwhelming case) are exact — return them as-is. Only decimal magnitudes can
    // carry floating-point dust (1.1 * 1e6 = 1100000.0000000002), so round just those to 6 places.
    return Number.isInteger(value) ? value : Math.round(value * 1e6) / 1e6;
};
